import { type OrderDetails, CONVEYANCE_TYPES } from '@koala/sdk';
import { FavoriteIcon } from '@/components/account/favorites/favoriteIcon';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { StyledFavoriteOrderCTA } from '@/components/orderDetail/location/header/styles';
import {
  StyledBasketItemContainer,
  StyledDate,
  StyledFavoriteCard,
  StyledInlineContent,
  StyledOrderTotal,
  StyledTitleAndHeartIconContainer,
} from '@/components/uielements/favoriteCard/styles';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { DATE_FORMAT } from '@/constants/dates';
import { API_FOR_CONVEYANCE_TYPES_SWAP } from '@/constants/global';
import { toDollars } from '@/utils/basket';
import { formatDate } from '@/utils/dates';
import SafeLink from '@/utils/custom_link';

interface Props {
  order: OrderDetails;
  index: number;
  reorderBasketId: string;
  reorderBasket: (order: OrderDetails) => void;
  showSubtotal?: boolean;
  showReceiptLink?: boolean;
  eventDetail: string;
}

export const FavoriteOrderCard = (props: Props) => {
  const {
    order,
    index,
    reorderBasketId,
    reorderBasket,
    showSubtotal = false,
    showReceiptLink = false,
    eventDetail,
  } = props;
  const orderConveyance =
    API_FOR_CONVEYANCE_TYPES_SWAP[order.order_data.basket.conveyance_type.type] ||
    CONVEYANCE_TYPES.PICKUP;

  return (
    <StyledFavoriteCard key={`${order.id}-${index}`}>
      {/* Order details and favorite button*/}
      <StyledTitleAndHeartIconContainer>
        <h3>{formatDate(order.order_data.order.ordered_at, DATE_FORMAT.WRITTEN_OUT_DATE)}</h3>
        <StyledFavoriteOrderCTA>
          <FavoriteIcon order={order} eventDetail={eventDetail} />
        </StyledFavoriteOrderCTA>
      </StyledTitleAndHeartIconContainer>
      <StyledDate>
        {orderConveyance} ordered from {order.store_location?.label}
      </StyledDate>

      {/* Basket Items*/}
      <StyledBasketItemContainer>
        {order.order_data.basket.basket_items.map((basketItem, idx) => (
          <span key={`${basketItem.id} - ${idx}`}>
            {basketItem.product.name}
            {basketItem.quantity > 1 && ` x${basketItem.quantity}`}
            {/* Add comma if not last item in the list */}
            {idx !== order.order_data.basket.basket_items.length - 1 && ', '}
          </span>
        ))}
      </StyledBasketItemContainer>

      <StyledInlineContent>
        {/* Subtotal */}
        {showSubtotal && (
          <StyledOrderTotal>Order Total: ${toDollars(order.total)}</StyledOrderTotal>
        )}
        {/* View Receipt link */}
        {showReceiptLink && (
          <SafeLink
            href={`/account/order-detail/${order.order_data.order.order_number}`}
            passHref={true}
            aria-label={`View Order #${order.order_data.order.order_number}`}
          >
            <StringAccessor accessor="order_history.view_reciept_button" />
          </SafeLink>
        )}
        {/* Reorder Button */}
        <StyledPrimaryButton
          data-css-override="StyledPrimaryButton"
          $submitting={reorderBasketId === order.id}
          onClick={() => reorderBasket(order)}
          aria-label={`Reorder Order #${order.order_data.order.order_number}`}
        >
          <StringAccessor accessor="order_history.reorder_button" />
        </StyledPrimaryButton>
      </StyledInlineContent>
    </StyledFavoriteCard>
  );
};
